import { type SearchDataTest } from '@/utils/data-test/search.data-test';
import { type FiltersDataTest } from '@/utils/data-test/filters.data-test';
import { type GlobalsDataTest } from '@/utils/data-test/globals.data-test';
import { type CartDataTest } from '@/utils/data-test/cart.data-test';
import { type GiftCardDataTest } from '@/utils/data-test/gift-card.data-test';
import { type ProductCardDataTest } from '@/utils/data-test/product-card.data-test';
import { type MenuDataTest } from '@/utils/data-test/menu.data-test';
import { type OnePageCheckoutDataTest } from '@/utils/data-test/one-page-checkout.data-test';
import { type ThankYouPageDataTest } from '@/utils/data-test/thank-you-page.data-test';
import { type ProductsDataTest } from '@/utils/data-test/products.data-test';
import { type PickupPointsDataTest } from '@/utils/data-test/pickup-points.data-test';
import { type ContactDataTest } from './contact.data-test';
import { type DynamicDataTest } from './components/dynamic.data-test';
import { type BaseSelectDropdownDataTest } from './components/baseSelectDropdown.data-test';
import { type GeolocationPopup } from './components/geolocationPopup';
import { type DynamicRadioDataTest } from '@/utils/data-test/dynamicRadiodata-test';
import { type CheckoutAddressStepDataTest } from './checkout-address-step.data-test';
import { type SummaryDataTest } from '@/utils/data-test/summary.data-test';
import { type LoaderDataTest } from '@/utils/data-test/components/loader.data-test';
import { type OutfitsDataTest } from './outfits.data-test';
import { type SimilarProductsPlaceholderDataTest } from './components/similarProductsPlaceholder.data-test';
import { type TopBarTest } from './top-bar.data-test';
import { type UpcomingReleasesTest } from '@/utils/data-test/upcoming-releases-test';
import { type PreviousReleasesTest } from '@/utils/data-test/previous-releases-test';
import { type NewsletterPopupDataTest } from './newsletter-popup.data-test';
import { type MyRafflesTest } from './my-raffles-test';
import { type MyRaffleTest } from './my-raffle-test';
import { type MyAccountDataTest } from './my-account-test';
import { type ReleaseCardDataTest } from '@/utils/data-test/release-card.data-test';
import { type DownloadDocumentButtonDataTest } from '@/utils/data-test/components/download-document-button.data-test';
import { type PurchaseReturnHistoryDetailsDataTest } from '@/utils/data-test/purchase-return-history-details';
import { type PurchaseReturnHistoryListDataTest } from '@/utils/data-test/purchase-return-history-list';
import { type RegisterVerificationDataTest } from '@/components/RegisterVerification/register-verification.data-test';
import { type HeaderPopupDataTest } from '@/utils/data-test/header.data-test';
import { type PageRowsDataTest } from './pageRows.data-test';
import { type TechnicalBreakDataTest } from './technicalBreakDataTest';
import { type SearchByImageDataTest } from './search-by-image.data-test';
import { type cookiesPopupDataTest } from './cookiesPopup.data-test';
import { type FooterDataTest } from '@/utils/data-test/footer.data-test';
import { type AutoVideo } from './auto-video.data-test';
import { type Pagination } from './components/pagination';
import { type MapSearchAutoComplete } from './components/map-search-autocomplete';
import { type PurchaseListItemsButtons } from './components/purchase-list-items-buttons';
import { type PurchaseCancellation } from './components/purchase-cancellation';
import { type Filters } from './filters';
import { type ProgramPremiumDataTest } from './program-premium.data-test';
import { type ModalDataTest } from '@/utils/data-test/modal.data-test';
import { type FavouriteDataTest } from '@/utils/data-test/favourites.data-test';
import { type BrandShopDataTest } from '@/utils/data-test/brandshop.data-test';
import { type MobileWidgetDataTest } from '@/utils/data-test/components/mobileWidget.data-test';
import { type SizeGroupProductDataTest } from '@/utils/data-test/sizeGroupProduct.data-test';
import { type ProductLabelsExtendedDataTest } from '@/utils/data-test/productLabelsExtended.data-test';
import { type SearchBrands } from './searchBrands';
import { type MapGeolocation } from './mapGeolocation';
import { type StepperButtons } from './components/stepper-buttons';
import { type MapList } from './components/map-list';
import { type BrandsFilter } from './components/brands-filter';
import { type BaseSelectItem } from './components/base-select-item';
import { type HistoryInfoTable } from './components/history-info-table';
import { type AnswearClubHistoryTable } from './components/answear-club-history-table';
import { type AnswearClubHistoryContent } from './components/answear-club-history-content';
import { type ProductItemSubmenuDataTest } from '@/utils/data-test/components/productItemSubmenu.data-test';
import { type LoginPanelDataTest } from '@/utils/data-test/loginPanel.data-test';
import { type ContentBoxDataTest } from '@/utils/data-test/content-box.data-test';
import { type PriceFilterContent } from './components/price-filter-content';
import { type MultiSelectSubmit } from './components/multi-select-submit';
import { type PriceDataTest } from './minimalPriceModal.data-test';
import { type LegacySizeChartDataTest } from './legacySizeChart.data-test';
import { type StaticLabelDataTest } from './staticLabelDataTest';
import { type BaseSelectDataTest } from './components/baseSelect.data-test';
import { type NewsletterPageDataTest } from '@/utils/data-test/newsletter-page.data-test';
import { type ProductDescriptionTabDataTest } from '@/utils/data-test/components/productDescriptionTab.data-test';
import { type ReturnsDataTest } from '@/utils/data-test/returns.data-test';
import { type MobileImageSearchResultDataTest } from './components/mobileImageSearchResult.data-test';
import { type ImageDataTest } from '@/utils/data-test/image.data-test';
import { type CountdownClockDataTest } from '@/utils/data-test/coundown-clock.data-test';
import { type NotificationsDataTest } from '@/utils/data-test/components/notifications.data-test';
import { type ProductsPaginationDataTest } from '@/utils/data-test/components/products-pagination.data-test';
import { type ToolTipDataTest } from '@/utils/data-test/tool-tip.data-test';
import { type CustomNotification } from './customNotifications.data-test';
import { type TopBannerDataTest } from './topBanner.data-test';
import { type BrandsDataTest } from './brands.data-test';
import { type InstagramSocialSectionData } from './instagramSocialSection.data-test';
import { type DeliveryData } from './delivery.data-test';
import { type ExpandableNumericCollectionDataTest } from '@/utils/data-test/components/expandable-numeric-collection.data-test';
import { type FaqDataTest } from '@/utils/data-test/faq.data-test';
import { type SliderDataTest } from '@/utils/data-test/slider.data-test';
import { type AutocompleteCategories } from './components/autocomplete-categories';
import { type AutocompleteHistoryDataTest } from './components/autocomplete-history';
import { type ProductSortDropdown } from './components/product-sort-dropdown';
import { type StaticPageDataTest } from '@/utils/data-test/static-page.data-test';
import { type ImageSearchResultWrapper } from './components/image-search-result-wrapper';
import { type MedicinePointsList } from './components/medicine-points-list';
import { type SaloonAvailabilityCheckerSelects } from './components/saloon-availability-checker-selects';
import { type ProductItemSearchWidget } from './components/product-item-search-widget';
import { type WidgetPrice } from './components/widget-price';
import { type AutoCompleteProducts } from './components/autocomplete-products';
import { type PriceFilterRangeInputs } from './components/price-filter-range-inputs';
import { type DynamicCheckbox } from './components/dynamic-checkbox';
import { type BannerAreaSlider } from './components/banner-area-slider';
import { type ProductsSliderWrapperDataTest } from './components/products-slider-wrapper';
import { type BannerSingleImageDataTest } from './components/banner-single-image';
import { type SizeChartMeasurementDataTest } from './components/size-chart-measurement';
import { type SizeChartModalDataType } from './components/size-chart-modal';
import { type AccordionHeaderDataTest } from '@/utils/data-test/accordionHeader.data-test';
import { type BuyTheLookDataTest } from '@/utils/data-test/buy-the-look.data-test';
import { type BrandSimilarProductsDataTest } from '@/utils/data-test/brand-similar-products';
import { type AnswearClub } from '@/utils/data-test/components/answear-club.data-test';
import { type PreSaleProductDataTest } from '@/utils/data-test/pre-sale-product.data-test';

export type DataTest =
    | GlobalsDataTest
    | SearchDataTest
    | FiltersDataTest
    | CartDataTest
    | GiftCardDataTest
    | ProductCardDataTest
    | SimilarProductsPlaceholderDataTest
    | MenuDataTest
    | OnePageCheckoutDataTest
    | ThankYouPageDataTest
    | ProductsDataTest
    | PickupPointsDataTest
    | ContactDataTest
    | DynamicDataTest
    | BaseSelectDropdownDataTest
    | GeolocationPopup
    | DynamicRadioDataTest
    | CheckoutAddressStepDataTest
    | LoaderDataTest
    | SummaryDataTest
    | OutfitsDataTest
    | TopBarTest
    | PreviousReleasesTest
    | UpcomingReleasesTest
    | NewsletterPopupDataTest
    | MyAccountDataTest
    | MyRafflesTest
    | MyRaffleTest
    | ReleaseCardDataTest
    | DownloadDocumentButtonDataTest
    | PurchaseReturnHistoryDetailsDataTest
    | PurchaseReturnHistoryListDataTest
    | RegisterVerificationDataTest
    | HeaderPopupDataTest
    | TechnicalBreakDataTest
    | PageRowsDataTest
    | SearchByImageDataTest
    | cookiesPopupDataTest
    | ProgramPremiumDataTest
    | ModalDataTest
    | FooterDataTest
    | FavouriteDataTest
    | LoginPanelDataTest
    | SearchBrands
    | MapGeolocation
    | Filters
    | MultiSelectSubmit
    | StepperButtons
    | MapList
    | BrandsFilter
    | BaseSelectItem
    | HistoryInfoTable
    | AnswearClubHistoryTable
    | AnswearClubHistoryContent
    | ProductItemSubmenuDataTest
    | ContentBoxDataTest
    | PriceFilterContent
    | Pagination
    | MapSearchAutoComplete
    | PurchaseListItemsButtons
    | PurchaseCancellation
    | BrandShopDataTest
    | MobileWidgetDataTest
    | PriceDataTest
    | LegacySizeChartDataTest
    | StaticLabelDataTest
    | BaseSelectDataTest
    | NewsletterPageDataTest
    | ImageDataTest
    | CountdownClockDataTest
    | SizeGroupProductDataTest
    | ProductLabelsExtendedDataTest
    | ProductDescriptionTabDataTest
    | ReturnsDataTest
    | MobileImageSearchResultDataTest
    | NotificationsDataTest
    | AutoVideo
    | ProductsPaginationDataTest
    | ToolTipDataTest
    | CustomNotification
    | TopBannerDataTest
    | BrandsDataTest
    | DeliveryData
    | InstagramSocialSectionData
    | ExpandableNumericCollectionDataTest
    | FaqDataTest
    | SliderDataTest
    | AnswearClub
    | AutocompleteCategories
    | ImageSearchResultWrapper
    | AutocompleteHistoryDataTest
    | ProductSortDropdown
    | MedicinePointsList
    | SaloonAvailabilityCheckerSelects
    | StaticPageDataTest
    | ProductItemSearchWidget
    | WidgetPrice
    | AutoCompleteProducts
    | PriceFilterRangeInputs
    | DynamicCheckbox
    | BannerAreaSlider
    | ProductsSliderWrapperDataTest
    | BannerSingleImageDataTest
    | SizeChartMeasurementDataTest
    | SizeChartModalDataType
    | AccordionHeaderDataTest
    | BrandSimilarProductsDataTest
    | BuyTheLookDataTest
    | PreSaleProductDataTest;

export const dataTestProperty = (source?: DataTest) => ({ 'data-test': source });
